import { match } from 'ts-pattern';
import { round } from '../util/round.js';
const getStackItemOutlineZones = (item)=>{
    return {
        id: item.id,
        start: item.x,
        end: item.x + item.width,
        type: 'outline'
    };
};
const getStackItemSwapZones = (item)=>{
    return {
        id: item.id,
        start: item.x + item.width / 6,
        end: item.x + item.width * 5 / 6,
        type: 'swap'
    };
};
const getZoneOverlap = (zone1, zone2)=>{
    return Math.min(zone1.end, zone2.end) - Math.max(zone1.start, zone2.start);
};
const areZonesOverlapping = (zone1, zone2)=>{
    // Round values first to prevent JavaScript floating point precision issues
    const zoneOneStart = round(zone1.start, 4);
    const zoneTwoStart = round(zone2.start, 4);
    const zoneOneEnd = round(zone1.end, 4);
    const zoneTwoEnd = round(zone2.end, 4);
    const areOverlapping = zoneOneStart > zoneTwoStart && zoneOneStart < zoneTwoEnd || zoneOneEnd > zoneTwoStart && zoneOneEnd < zoneTwoEnd;
    return areOverlapping;
};
const getOutlineIntersections = (zones, evaluatedZone)=>{
    return zones.filter((zone)=>zone.id !== evaluatedZone.id && areZonesOverlapping(zone, evaluatedZone)).map((zone)=>({
            zone,
            overlap: getZoneOverlap(zone, evaluatedZone),
            direction: zone.start < evaluatedZone.start ? 'right' : 'left'
        }));
};
const getSwapIntersections = (zones, evaluatedZone, originalPosition)=>{
    return zones.filter((zone)=>zone.id !== evaluatedZone.id && areZonesOverlapping(zone, evaluatedZone)).map((zone)=>({
            zone,
            overlap: getZoneOverlap(zone, evaluatedZone),
            direction: zone.start < originalPosition ? 'right' : 'left'
        }));
};
const getActiveIntersection = (sortedStack, draggedItem, originalPosition)=>{
    const draggedItemOutlineZones = getStackItemOutlineZones(draggedItem);
    const draggedItemSwapZones = getStackItemSwapZones(draggedItem);
    const stackItemOutlineZones = sortedStack.map((item)=>getStackItemOutlineZones(item));
    const stackItemSwapZones = sortedStack.map((item)=>getStackItemSwapZones(item));
    const outlineIntersections = getOutlineIntersections(stackItemOutlineZones, draggedItemOutlineZones);
    const swapIntersections = getSwapIntersections(stackItemSwapZones, draggedItemSwapZones, originalPosition);
    if (swapIntersections.length > 0) {
        return swapIntersections.sort((a, b)=>b.overlap - a.overlap)[0];
    }
    if (outlineIntersections.length > 0) {
        return outlineIntersections.sort((a, b)=>b.overlap - a.overlap)[0];
    }
    return null;
};
const getFreeSpaceStack = (sortedStack, draggedItem)=>{
    const exists = sortedStack.some((item)=>item.id === draggedItem.id);
    const newStack = exists ? sortedStack.map((item)=>{
        if (item.id === draggedItem.id) {
            return {
                ...item,
                x: draggedItem.x
            };
        }
        return item;
    }) : [
        ...sortedStack,
        draggedItem
    ];
    return newStack.sort((a, b)=>a.x - b.x);
};
const getSwappedStack = (sortedStack, draggedItem, activeIntersection)=>{
    const items = [];
    for (const item of sortedStack){
        if (item.id === draggedItem.id) {
            continue;
        }
        if (item.id === activeIntersection.zone.id) {
            if (activeIntersection.direction === 'left') {
                items.push(item);
                items.push({
                    ...draggedItem,
                    x: item.x
                });
            } else {
                items.push({
                    ...draggedItem,
                    x: item.x
                });
                items.push(item);
            }
            continue;
        }
        items.push(item);
    }
    return items;
};
const getSnappedStack = (sortedStack, draggedItem, activeIntersection)=>{
    const items = [];
    for (const item of sortedStack){
        if (item.id === draggedItem.id) {
            continue;
        }
        if (item.id === activeIntersection.zone.id) {
            if (activeIntersection.direction === 'right') {
                items.push(item);
                items.push({
                    ...draggedItem,
                    x: activeIntersection.zone.start
                });
            } else {
                items.push({
                    ...draggedItem,
                    x: activeIntersection.zone.start
                });
                items.push(item);
            }
            continue;
        }
        items.push(item);
    }
    return items;
};
const fixStackFromLeft = (sortedStack, limits)=>{
    const items = [];
    let minValue = limits.min;
    for (const item of sortedStack){
        const x = item.x < minValue ? minValue : item.x;
        items.push({
            ...item,
            x
        });
        minValue = x + item.width;
    }
    return items;
};
const fixStackFromRight = (sortedStack, limits)=>{
    const items = [];
    let maxValue = limits.max;
    for (const item of sortedStack.slice().reverse()){
        const x = item.x + item.width > maxValue ? maxValue - item.width : item.x;
        items.push({
            ...item,
            x
        });
        maxValue = x;
    }
    return items.sort((a, b)=>a.x - b.x);
};
export const isStackValid = (stack, stackLimits)=>{
    for (const item of stack){
        for (const otherItem of stack){
            if (item.id === otherItem.id) {
                continue;
            }
            const outlineZone = getStackItemOutlineZones(item);
            const otherOutlineZone = getStackItemOutlineZones(otherItem);
            if (areZonesOverlapping(outlineZone, otherOutlineZone)) {
                return false;
            }
        }
    }
    return stack.every((item)=>item.x >= stackLimits.min && round(item.x + item.width, 2) <= stackLimits.max);
};
export const getPositionedStack = (stack, draggedEntity, whitelistedSpaces)=>{
    const sortedStackItems = stack.items.slice().sort((a, b)=>a.x - b.x);
    const draggedItemIndex = stack.items.findIndex((item)=>item.id === draggedEntity.id);
    const originalDraggedItem = stack.items[draggedItemIndex];
    // TODO: Handle snapping?
    const activeIntersection = getActiveIntersection(sortedStackItems, draggedEntity, originalDraggedItem?.x ?? Infinity);
    const newStack = match(activeIntersection).with(null, ()=>{
        const newStack = getFreeSpaceStack(sortedStackItems, draggedEntity);
        return fixStackFromLeft(fixStackFromRight(newStack, stack.limits), stack.limits);
    }).with({
        zone: {
            type: 'swap'
        }
    }, (activeIntersection)=>{
        const newStack = getSwappedStack(sortedStackItems, draggedEntity, activeIntersection);
        return activeIntersection.direction === 'right' ? fixStackFromRight(fixStackFromLeft(newStack, stack.limits), stack.limits) : fixStackFromLeft(fixStackFromRight(newStack, stack.limits), stack.limits);
    }).with({
        zone: {
            type: 'outline'
        }
    }, (activeIntersection)=>{
        const newStack = getSnappedStack(sortedStackItems, draggedEntity, activeIntersection);
        return activeIntersection.direction === 'right' ? fixStackFromRight(fixStackFromLeft(newStack, stack.limits), stack.limits) : fixStackFromLeft(fixStackFromRight(newStack, stack.limits), stack.limits);
    }).exhaustive();
    if (draggedItemIndex !== -1 && newStack.length !== stack.items.length) {
        throw new Error(`Stack length mismatch, this should not happen!`);
    }
    if (draggedItemIndex === -1 && newStack.length !== stack.items.length + 1) {
        throw new Error(`Stack length mismatch while adding a new item, this should not happen!`);
    }
    // If whitelisted spaces are provided, validate that the dragged item is within one of them
    if (whitelistedSpaces && whitelistedSpaces.length > 0) {
        const draggedItemInNewStack = newStack.find((item)=>item.id === draggedEntity.id);
        if (draggedItemInNewStack) {
            // Filter out spaces that are too small to fit the item
            const validSpaces = whitelistedSpaces.filter((space)=>space.maxX - space.minX >= draggedItemInNewStack.width);
            // If no valid spaces exist, revert to original position
            if (validSpaces.length === 0) {
                if (originalDraggedItem) {
                    return newStack.map((item)=>item.id === draggedEntity.id ? originalDraggedItem : item);
                } else {
                    return sortedStackItems;
                }
            }
            const itemStart = draggedItemInNewStack.x;
            const itemEnd = draggedItemInNewStack.x + draggedItemInNewStack.width;
            // Check if the item is fully contained within any valid whitelisted space
            const isInWhitelistedSpace = validSpaces.some((space)=>itemStart >= space.minX && itemEnd <= space.maxX);
            if (!isInWhitelistedSpace) {
                // Find the closest valid space and snap to it
                let closestSpace = null;
                let minDistance = Infinity;
                for (const space of validSpaces){
                    // Calculate distance from item's center to space's center
                    const itemCenter = itemStart + draggedItemInNewStack.width / 2;
                    const spaceCenter = (space.minX + space.maxX) / 2;
                    const distance = Math.abs(itemCenter - spaceCenter);
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestSpace = space;
                    }
                }
                if (closestSpace) {
                    // Calculate the best position within the closest space
                    let snappedX = itemStart;
                    // Item fits - find best position within the space
                    if (itemStart < closestSpace.minX) {
                        // Item is to the left of the space - snap to left edge
                        snappedX = closestSpace.minX;
                    } else if (itemEnd > closestSpace.maxX) {
                        // Item is to the right of the space - snap to right edge
                        snappedX = closestSpace.maxX - draggedItemInNewStack.width;
                    } else {
                        // Item center is within space bounds but extends outside - clamp it
                        snappedX = Math.max(closestSpace.minX, Math.min(itemStart, closestSpace.maxX - draggedItemInNewStack.width));
                    }
                    // Update the item's position in the stack
                    return newStack.map((item)=>item.id === draggedEntity.id ? {
                            ...item,
                            x: snappedX
                        } : item);
                } else if (originalDraggedItem) {
                    // No valid space found - revert to original (shouldn't happen if validSpaces exist)
                    return newStack.map((item)=>item.id === draggedEntity.id ? originalDraggedItem : item);
                } else {
                    // If item is new and not in a valid space, filter it out
                    return sortedStackItems;
                }
            }
        }
    }
    return newStack;
};
export const getEmptySpace = (stack, width, customLimits)=>{
    const effectiveLimits = customLimits ?? stack.limits;
    const sortedItems = stack.items.slice().sort((a, b)=>a.x - b.x);
    if (sortedItems.length === 0) {
        if (effectiveLimits.max - effectiveLimits.min >= width) {
            return {
                start: effectiveLimits.min,
                end: effectiveLimits.min + width
            };
        }
        return null;
    }
    const firstItem = sortedItems[0];
    if (firstItem.x > effectiveLimits.min) {
        const gapWidthBeforeFirst = firstItem.x - effectiveLimits.min;
        if (gapWidthBeforeFirst >= width) {
            const preferredSlotStart = firstItem.x - width;
            if (preferredSlotStart >= effectiveLimits.min) {
                return {
                    start: preferredSlotStart,
                    end: preferredSlotStart + width
                };
            } else {
                return {
                    start: effectiveLimits.min,
                    end: effectiveLimits.min + width
                };
            }
        }
    }
    for(let i = 0; i < sortedItems.length - 1; i++){
        const prevItem = sortedItems[i];
        const nextItem = sortedItems[i + 1];
        const gapBetweenItemsStart = prevItem.x + prevItem.width;
        const gapBetweenItemsEnd = nextItem.x;
        if (gapBetweenItemsEnd > gapBetweenItemsStart) {
            const actualGapWidth = gapBetweenItemsEnd - gapBetweenItemsStart;
            if (actualGapWidth >= width) {
                const preferredSlotStart = nextItem.x - width;
                if (preferredSlotStart >= gapBetweenItemsStart) {
                    return {
                        start: preferredSlotStart,
                        end: preferredSlotStart + width
                    };
                } else {
                    return {
                        start: gapBetweenItemsStart,
                        end: gapBetweenItemsStart + width
                    };
                }
            }
        }
    }
    const lastItem = sortedItems[sortedItems.length - 1];
    const spaceAfterLastStart = lastItem.x + lastItem.width;
    if (effectiveLimits.max > spaceAfterLastStart) {
        const gapWidthAfterLast = effectiveLimits.max - spaceAfterLastStart;
        if (gapWidthAfterLast >= width) {
            return {
                start: spaceAfterLastStart,
                end: spaceAfterLastStart + width
            };
        }
    }
    return null;
};
export const freeSpaceLeft = (itemId, stack)=>{
    const sortedStack = stack.items.slice().sort((a, b)=>a.x - b.x);
    const itemIndex = sortedStack.findIndex((item)=>item.id === itemId);
    const item = sortedStack[itemIndex];
    if (!item) {
        return 0;
    }
    const prevItem = sortedStack[itemIndex - 1];
    if (!prevItem) {
        return item.x - stack.limits.min;
    }
    return item.x - (prevItem.x + prevItem.width);
};
export const freeSpaceRight = (itemId, stack)=>{
    const sortedStack = stack.items.slice().sort((a, b)=>a.x - b.x);
    const itemIndex = sortedStack.findIndex((item)=>item.id === itemId);
    const item = sortedStack[itemIndex];
    if (!item) {
        return 0;
    }
    const nextItem = sortedStack[itemIndex + 1];
    if (!nextItem) {
        return stack.limits.max - (item.x + item.width);
    }
    return nextItem.x - (item.x + item.width);
};
// Constant for floating point comparisons in snapping logic
const SNAP_EPSILON = 0.001;
export const isItemSnappedFromLeft = (itemId, stack)=>{
    const currentItem = stack.items.find((item)=>item.id === itemId);
    if (!currentItem) {
        // If the item is not found in the stack, it cannot be snapped.
        return false;
    }
    // Check if snapped to another item on the left
    const isSnappedToOtherItem = stack.items.some((otherItem)=>{
        if (otherItem.id === currentItem.id) {
            return false // Don't compare with itself
            ;
        }
        // Check if otherItem ends precisely where currentItem begins
        return Math.abs(otherItem.x + otherItem.width - currentItem.x) < SNAP_EPSILON;
    });
    return isSnappedToOtherItem // True if snapped to another item, false if space to the left relative to other items
    ;
};
export const isItemSnappedFromRight = (itemId, stack)=>{
    const currentItem = stack.items.find((item)=>item.id === itemId);
    if (!currentItem) {
        // If the item is not found in the stack, it cannot be snapped.
        return false;
    }
    const currentItemEnd = currentItem.x + currentItem.width;
    // Check if snapped to another item on the right
    const isSnappedToOtherItem = stack.items.some((otherItem)=>{
        if (otherItem.id === currentItem.id) {
            return false // Don't compare with itself
            ;
        }
        // Check if otherItem begins precisely where currentItem ends
        return Math.abs(otherItem.x - currentItemEnd) < SNAP_EPSILON;
    });
    return isSnappedToOtherItem // True if snapped to another item, false if space to the right relative to other items
    ;
};
export const isAtTheLeftEdge = (itemId, stack)=>{
    const currentItem = stack.items.find((item)=>item.id === itemId);
    if (!currentItem) {
        // If the item is not found in the stack, it cannot be at the edge.
        return false;
    }
    // Check if the item's x is at the left edge (stack.limits.min)
    return Math.abs(currentItem.x - stack.limits.min) < SNAP_EPSILON;
};
export const isAtTheRightEdge = (itemId, stack)=>{
    const currentItem = stack.items.find((item)=>item.id === itemId);
    if (!currentItem) {
        // If the item is not found in the stack, it cannot be at the edge.
        return false;
    }
    // Check if the item's end is at the right edge (stack.limits.max)
    return Math.abs(currentItem.x + currentItem.width - stack.limits.max) < SNAP_EPSILON;
};
export const isAtTheEdge = (itemId, stack)=>{
    return isAtTheLeftEdge(itemId, stack) || isAtTheRightEdge(itemId, stack);
};

//# sourceMappingURL=stack.js.map